import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Database, Shield, Users, Zap, Check } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";

export default function LandingPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: Database,
      title: "Connect Any Airtable Base",
      description: "Import your existing Airtable bases in seconds. No migration needed."
    },
    {
      icon: Shield,
      title: "Secure Client Access",
      description: "Control exactly what each client sees with field-level permissions."
    },
    {
      icon: Users,
      title: "No Extra Seats",
      description: "Share data without paying for additional Airtable collaborator seats."
    },
    {
      icon: Zap,
      title: "Live in Minutes",
      description: "Create branded portals instantly. No code or complex setup required."
    }
  ];

  const benefits = [
    "Unlimited client portals",
    "Custom branding per portal",
    "Real-time data sync",
    "Row-level filtering",
    "Share links or invite by email",
    "Mobile-responsive views"
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Database className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold tracking-tight">Paeora</span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button
              variant="ghost"
              className="text-foreground hover:bg-muted"
              onClick={() => navigate("/login")}
              data-testid="header-login-btn"
            >
              Sign In
            </Button>
            <Button
              className="btn-primary px-6"
              onClick={() => navigate("/register")}
              data-testid="header-register-btn"
            >
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24 pb-32 px-4 md:px-8 overflow-hidden">
        <div className="hero-glow" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-widest text-primary mb-4 animate-fade-in">
              The smarter way to share Airtable data
            </p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 animate-slide-up">
              Share Airtable data{" "}
              <span className="text-accent">without paying</span>{" "}
              for extra seats.
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 animate-slide-up stagger-1">
              Turn any Airtable base into a secure, branded client portal — live in minutes. 
              No code, no setup, no extra seat costs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-slide-up stagger-2">
              <Button
                className="btn-primary px-8 py-6 text-lg"
                onClick={() => navigate("/register")}
                data-testid="hero-get-started-btn"
              >
                Start Free <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                variant="outline"
                className="btn-secondary px-8 py-6 text-lg"
                onClick={() => navigate("/login")}
                data-testid="hero-signin-btn"
              >
                Sign In
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Gradient beam divider */}
      <div className="gradient-beam w-full" />

      {/* Features Grid */}
      <section className="py-24 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-primary mb-4">Features</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">
              Everything you need to share data
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-lg p-8 card-hover group"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center mb-6 group-hover:bg-accent transition-colors duration-300">
                  <feature.icon className="w-6 h-6 text-primary group-hover:text-accent-foreground transition-colors duration-300" />
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-4 md:px-8 bg-card/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-primary mb-4">Why Paeora</p>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                Better and more affordable than alternatives
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Stop paying for extra Airtable seats just to let clients view their data. 
                Create unlimited portals with custom branding and granular access control.
              </p>
              <Button
                className="btn-primary px-8"
                onClick={() => navigate("/register")}
                data-testid="benefits-get-started-btn"
              >
                Get Started Free
              </Button>
            </div>
            <div className="bg-card border border-border rounded-lg p-8">
              <h3 className="text-xl font-bold mb-6">What you get</h3>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-accent rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-accent-foreground" />
                    </div>
                    <span className="text-muted-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 md:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
            Ready to share your data?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Create your first client portal in under 5 minutes.
          </p>
          <Button
            className="btn-primary px-10 py-6 text-lg animate-pulse-glow"
            onClick={() => navigate("/register")}
            data-testid="cta-get-started-btn"
          >
            Start Free <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-4 md:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-primary rounded-md flex items-center justify-center">
              <Database className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-bold">Paeora</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2026 Paeora. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
